import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/api';

const extractYouTubeId = (url) => {
  if (!url) return '';

  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&\s]+)/
  );

  return match ? match[1] : '';
};

const buildParagraphs = (content = '') => {
  return content
    .replace(/\r\n/g, '\n')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const BlogArticle = () => {
  const { slug } = useParams();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/blog/articles/${slug}`)
      .then((res) => {
        setArticle(res.data);
      })
      .catch(() => {
        setArticle(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const paragraphs = useMemo(
    () => buildParagraphs(article?.content),
    [article?.content]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Cargando...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#000000] mb-4">
            Artículo no encontrado
          </h1>

          <Link
            to="/blog"
            className="text-[#000000] font-bold hover:underline"
          >
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  const ytId = extractYouTubeId(article.youtube_url);

  return (
    <main className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-5 sm:px-6 py-10 md:py-14">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#000000] hover:opacity-60 mb-8 font-medium transition-opacity"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Actualidad Pet
        </Link>

        {article.category && (
          <div className="mb-5">
            <span
              className="inline-block text-xs font-bold uppercase tracking-wider text-[#000000] bg-[#ffff00] px-3 py-1.5 rounded-full"
              data-testid="article-category"
            >
              {article.category}
            </span>
          </div>
        )}

        <h1
          className="text-4xl md:text-5xl font-extrabold text-[#000000] leading-tight tracking-tight mb-8"
          data-testid="article-title"
        >
          {article.title}
        </h1>

        {article.image && (
          <figure className="aspect-video rounded-2xl md:rounded-3xl overflow-hidden mb-10 bg-gray-100">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </figure>
        )}

        {ytId && (
          <div
            className="relative w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-lg mb-10"
            data-testid="article-youtube"
          >
            <div className="relative w-full aspect-[16/9]">
              <iframe
                src={`https://www.youtube.com/embed/${ytId}`}
                title={article.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          <div className="space-y-7 text-[18px] md:text-[20px] leading-[1.9] text-gray-700">
            {paragraphs.map((paragraph, index) => (
              <p key={`${index}-${paragraph.slice(0, 20)}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>
    </main>
  );
};

export default BlogArticle;
